import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import "../App.css";
import {mobile} from "../services/mobileBridge";

/* ============================================================
   WORD LADDERS (DOUBLETS) — a game in the spirit of Lewis Carroll
   ------------------------------------------------------------
   Transform the START word into the END word, one letter at a
   time. Every step must be a real English word. Sound, streaks,
   hints, a timer and confetti included.
   ============================================================ */

type Country = "south-africa" | "nigeria";

const WORD_LIST_URL =
  "https://raw.githubusercontent.com/dwyl/english-words/master/words_alpha.txt";

// Curated "classic" doublets — guaranteed solvable, famous, and fun.
const CLASSIC_DOUBLETS: { start: string; end: string; story: string }[] = [
  { start: "cold", end: "warm", story: "Lewis Carroll's original Doublet." },
  { start: "cat", end: "dog", story: "The timeless rivalry." },
  { start: "love", end: "hate", story: "A change of heart." },
  { start: "dark", end: "dawn", story: "From night to morning light." },
  { start: "black", end: "white", story: "Two sides of the spectrum." },
  { start: "fire", end: "wood", story: "What feeds the flame." },
  { start: "less", end: "more", story: "Minimalism vs maximalism." },
  { start: "warm", end: "cold", story: "The reverse chill." },
  { start: "root", end: "tree", story: "From seed to sky." },
  { start: "east", end: "west", story: "Around the compass." },
  { start: "fish", end: "bird", story: "Sea meets sky." },
  { start: "lead", end: "gold", story: "The alchemist's dream." },
  { start: "ape", end: "man", story: "Evolution in three steps." },
  { start: "four", end: "five", story: "Counting up." },
  { start: "pig", end: "sty", story: "Home sweet home." },
];

// Tiny fallback dictionary so the game still works offline / if fetch fails.
const FALLBACK_DICT = [
  "cold", "cord", "card", "ward", "warm", "core", "care", "ware", "wore",
  "word", "wood", "wool", "cool", "pool", "foil", "foul", "soul", "soup",
  "soap", "soak", "sock", "sick", "silk", "milk", "mill", "mull", "muse",
  "must", "mast", "most", "cost", "cast", "cart", "care", "cure", "pure",
  "fire", "five", "dive", "dive", "live", "love", "hate", "have", "haze",
  "dark", "bark", "barn", "burn", "born", "corn", "cord", "word", "ward",
  "fish", "fist", "fast", "cast", "cart", "part", "port", "sort", "soft",
  "east", "easy", "essay", "west", "best", "rest", "rust", "bust", "burn",
  "lead", "load", "goad", "gold", "good", "wood", "wool",
  "cat", "cot", "cog", "dog", "dot", "cot", "cap", "tap", "top", "tip",
  "ape", "apt", "opt", "out", "rut", "run", "bun", "ban", "man", "men",
  "four", "tour", "your", "pour", "sour", "soup", "foul", "five", "give",
];

const HINT_COST = 25; // points deducted per hint
const TIME_BONUS_PER_SEC_LEFT = 1;
const STEP_BONUS = 10;

// ---------- Audio (Web Audio API — no external files) ----------
function useSound() {
  const ctxRef = useRef<AudioContext | null>(null);
  const getCtx = () => {
    if (!ctxRef.current) {
      const AC = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext;
      ctxRef.current = new AC();
    }
    return ctxRef.current;
  };
  const tone = (freq: number, dur: number, type: OscillatorType = "sine", gain = 0.08, delay = 0) => {
    try {
      const ctx = getCtx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = type;
      o.frequency.value = freq;
      o.connect(g);
      g.connect(ctx.destination);
      const t = ctx.currentTime + delay;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(gain, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      o.start(t);
      o.stop(t + dur + 0.02);
    } catch { /* ignore */ }
  };
  return {
    correct: () => { tone(523.25, 0.12, "triangle"); tone(783.99, 0.16, "triangle", 0.07, 0.07); },
    wrong: () => { tone(196, 0.18, "sawtooth", 0.06); tone(155, 0.22, "sawtooth", 0.05, 0.09); },
    click: () => tone(880, 0.04, "square", 0.03),
    win: () => {
      [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => tone(f, 0.18, "triangle", 0.08, i * 0.09));
    },
    lose: () => { tone(311, 0.25, "sawtooth", 0.07); tone(233, 0.4, "sawtooth", 0.06, 0.12); },
  };
}

// ---------- Dictionary loading & neighbor graph ----------
type DictState = {
  byLen: Map<number, Set<string>>;
  buckets: Map<number, Map<string, string[]>>; // length -> bucket -> words
};

function buildBuckets(words: string[], len: number) {
  const map = new Map<string, string[]>();
  for (const w of words) {
    if (w.length !== len) continue;
    for (let i = 0; i < len; i++) {
      const key = w.slice(0, i) + "*" + w.slice(i + 1);
      const arr = map.get(key);
      if (arr) arr.push(w); else map.set(key, [w]);
    }
  }
  return map;
}

function neighbors(word: string, buckets: Map<string, string[]>, len: number): string[] {
  const out = new Set<string>();
  for (let i = 0; i < len; i++) {
    const key = word.slice(0, i) + "*" + word.slice(i + 1);
    const arr = buckets.get(key);
    if (arr) for (const n of arr) if (n !== word) out.add(n);
  }
  return Array.from(out);
}

// BFS shortest path; returns map of word->distance from start.
function bfsDistances(start: string, buckets: Map<string, string[]>, len: number): Map<string, number> {
  const dist = new Map<string, number>();
  dist.set(start, 0);
  const q: string[] = [start];
  while (q.length) {
    const cur = q.shift()!;
    const d = dist.get(cur)!;
    for (const n of neighbors(cur, buckets, len)) {
      if (!dist.has(n)) {
        dist.set(n, d + 1);
        q.push(n);
      }
    }
  }
  return dist;
}

function oneBfsPath(start: string, end: string, buckets: Map<string, string[]>, len: number): string[] | null {
  const prev = new Map<string, string | null>();
  prev.set(start, null);
  const q: string[] = [start];
  while (q.length) {
    const cur = q.shift()!;
    if (cur === end) {
      const path: string[] = [];
      let c: string | null = cur;
      while (c) { path.unshift(c); c = prev.get(c) ?? null; }
      return path;
    }
    for (const n of neighbors(cur, buckets, len)) {
      if (!prev.has(n)) { prev.set(n, cur); q.push(n); }
    }
  }
  return null;
}

// ---------- Puzzle generation ----------
function makePuzzle(dict: DictState, len: number, difficulty: number): { start: string; end: string; par: number } | null {
  const buckets = dict.buckets.get(len);
  if (!buckets) return null;
  const words = dict.byLen.get(len);
  if (!words || words.size < 50) return null;

  // try a handful of random starts, BFS, pick an end at target distance
  for (let attempt = 0; attempt < 30; attempt++) {
    const arr = Array.from(words);
    const start = arr[Math.floor(Math.random() * arr.length)];
    const dist = bfsDistances(start, buckets, len);
    // candidates at distance >= difficulty and reasonable
    const candidates: { w: string; d: number }[] = [];
    dist.forEach((d, w) => {
      if (d >= difficulty && d <= difficulty + 3) candidates.push({ w, d });
    });
    if (candidates.length === 0) continue;
    const pick = candidates[Math.floor(Math.random() * candidates.length)];
    return { start, end: pick.w, par: pick.d };
  }
  return null;
}

// ---------- Confetti ----------
type Confetti = { x: number; y: number; vx: number; vy: number; life: number; color: string; size: number; rot: number; vr: number };
function useConfetti() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Confetti[]>([]);
  const rafRef = useRef<number>(0);
  const runningRef = useRef(false);

  const loop = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d")!;
    const W = cv.width, H = cv.height;
    ctx.clearRect(0, 0, W, H);
    const ps = particlesRef.current;
    for (let i = ps.length - 1; i >= 0; i--) {
      const p = ps[i];
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.12;
      p.rot += p.vr;
      p.life -= 1;
      if (p.y > H + 20 || p.life <= 0) { ps.splice(i, 1); continue; }
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.globalAlpha = Math.min(1, p.life / 60);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.5);
      ctx.restore();
    }
    if (ps.length === 0) {
      runningRef.current = false;
      ctx.clearRect(0, 0, W, H);
      return;
    }
    rafRef.current = requestAnimationFrame(loop);
  }, []);

  const burst = useCallback((count = 140) => {
    const cv = canvasRef.current;
    if (!cv) return;
    const colors = ["#3ddbd9", "#48b1f0", "#ff5470", "#fbbf24", "#4ade80", "#a78bfa", "#ffffff"];
    for (let i = 0; i < count; i++) {
      particlesRef.current.push({
        x: cv.width / 2 + (Math.random() - 0.5) * 80,
        y: cv.height / 2,
        vx: (Math.random() - 0.5) * 9,
        vy: Math.random() * -8 - 3,
        life: 110 + Math.random() * 50,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 6 + Math.random() * 6,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.4,
      });
    }
    if (!runningRef.current) {
      runningRef.current = true;
      rafRef.current = requestAnimationFrame(loop);
    }
  }, [loop]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);
  return { canvasRef, burst };
}

// ---------- Component ----------
type Phase = "menu" | "playing" | "won" | "lost";

export default function WordLadders({ country }: { country: Country }) {
  const [dict, setDict] = useState<DictState | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadingError] = useState(false);

  const [phase, setPhase] = useState<Phase>("menu");
  const [len, setLen] = useState(4);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [par, setPar] = useState(0);
  const [story, setStory] = useState("");
  const [ladder, setLadder] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [shake, setShake] = useState(0);
  const [badWord, setBadWord] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [hints, setHints] = useState(0);
  const [showHint, setShowHint] = useState<string | null>(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [highScore, setHighScore] = useState(() => Number(localStorage.getItem("wordLaddersHi") || 0));
  const [bestStreak, setBestStreak] = useState(() => Number(localStorage.getItem("wordLaddersStreak") || 0));
  const [solution, setSolution] = useState<string[] | null>(null);
  const [solving, setSolving] = useState(false);

  const sound = useSound();
  const { canvasRef, burst } = useConfetti();
  const timerRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ---- load dictionary ----
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(WORD_LIST_URL)
      .then(r => r.text())
      .then(text => {
        if (cancelled) return;
        const words = text.split(/\r?\n/).map(w => w.trim().toLowerCase()).filter(w => /^[a-z]+$/.test(w));
        const byLen = new Map<number, Set<string>>();
        const buckets = new Map<number, Map<string, string[]>>();
        for (const w of words) {
          let s = byLen.get(w.length);
          if (!s) { s = new Set(); byLen.set(w.length, s); }
          s.add(w);
        }
        for (const L of [3, 4, 5, 6]) {
          const ws = Array.from(byLen.get(L) ?? []);
          if (ws.length) buckets.set(L, buildBuckets(ws, L));
        }
        setDict({ byLen, buckets });
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        // fallback
        const byLen = new Map<number, Set<string>>();
        const buckets = new Map<number, Map<string, string[]>>();
        const clean = Array.from(new Set(FALLBACK_DICT));
        for (const w of clean) {
          let s = byLen.get(w.length);
          if (!s) { s = new Set(); byLen.set(w.length, s); }
          s.add(w);
        }
        for (const L of [3, 4, 5]) buckets.set(L, buildBuckets(Array.from(byLen.get(L) ?? []), L));
        setDict({ byLen, buckets });
        setLoading(false);
        setLoadingError(true);
      });
    return () => { cancelled = true; };
  }, []);

  // ---- timer ----
  useEffect(() => {
    if (phase !== "playing") return;
    timerRef.current = window.setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          window.clearInterval(timerRef.current!);
          setPhase("lost");
          sound.lose();
          mobile.haptic("gameover");
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) window.clearInterval(timerRef.current); };
  }, [phase, sound]);

  useEffect(() => {
    if (phase === "playing") inputRef.current?.focus();
  }, [phase, ladder.length]);

  const isValidWord = useCallback((w: string) => {
    if (!dict) return false;
    const s = dict.byLen.get(w.length);
    return !!s && s.has(w.toLowerCase());
  }, [dict]);

  const startGame = useCallback((wordLen: number, difficulty: number, classic?: boolean) => {
    if (!dict) return;
    setLen(wordLen);
    let puzzle: { start: string; end: string; par: number } | null = null;
    let pickedStory = "";
    if (classic) {
      const opts = CLASSIC_DOUBLETS.filter(d => d.start.length === wordLen && d.end.length === wordLen);
      const c = opts[Math.floor(Math.random() * opts.length)];
      const path = oneBfsPath(c.start, c.end, dict.buckets.get(wordLen)!, wordLen);
      puzzle = { start: c.start, end: c.end, par: path ? path.length - 1 : 6 };
      pickedStory = c.story;
    } else {
      puzzle = makePuzzle(dict, wordLen, difficulty);
    }
    if (!puzzle) {
      // fall back to classic if generator fails
      const opts = CLASSIC_DOUBLETS.filter(d => d.start.length === wordLen && d.end.length === wordLen);
      const c = opts[Math.floor(Math.random() * opts.length)];
      const path = oneBfsPath(c.start, c.end, dict.buckets.get(wordLen)!, wordLen);
      puzzle = { start: c.start, end: c.end, par: path ? path.length - 1 : 6 };
      pickedStory = c.story;
    }
    const timeBudget = 20 + puzzle.par * 18;
    setStart(puzzle.start);
    setEnd(puzzle.end);
    setPar(puzzle.par);
    setStory(pickedStory);
    setLadder([puzzle.start]);
    setInput("");
    setShowHint(null);
    setHints(0);
    setBadWord(false);
    setTimeLeft(timeBudget);
    setSolving(false);
    setSolution(null);
    setPhase("playing");
    sound.click();
  }, [dict, sound]);

  const submitStep = () => {
    const guess = input.trim().toLowerCase();
    if (!guess) return;
    const last = ladder[ladder.length - 1];
    if (guess === last) { setShake(s => s + 1); sound.wrong(); mobile.haptic("warning"); return; }
    if (guess.length !== len) { setShake(s => s + 1); setBadWord(true); sound.wrong(); mobile.haptic("warning"); return; }
    // must differ by exactly one letter
    let diff = 0;
    for (let i = 0; i < len; i++) if (guess[i] !== last[i]) diff++;
    if (diff !== 1) { setShake(s => s + 1); setBadWord(true); sound.wrong(); mobile.haptic("warning"); return; }
    if (!isValidWord(guess)) { setShake(s => s + 1); setBadWord(true); sound.wrong(); mobile.haptic("warning"); return; }

    const next = [...ladder, guess];
    setLadder(next);
    setInput("");
    setBadWord(false);
    sound.correct();
    mobile.haptic("light");

    if (guess === end) {
      // win!
      if (timerRef.current) window.clearInterval(timerRef.current);
      const stepsUsed = next.length - 1;
      const base = stepsUsed * STEP_BONUS;
      const parBonus = Math.max(0, (par - (stepsUsed - par)) * 5); // reward optimal play
      const timeBonus = timeLeft * TIME_BONUS_PER_SEC_LEFT;
      const gained = base + parBonus + timeBonus;
      const newScore = score + gained;
      const newStreak = streak + 1;
      setScore(newScore);
      setStreak(newStreak);
      if (newScore > highScore) { setHighScore(newScore); localStorage.setItem("wordLaddersHi", String(newScore)); }
      if (newStreak > bestStreak) { setBestStreak(newStreak); localStorage.setItem("wordLaddersStreak", String(newStreak)); }
      setPhase("won");
      sound.win();
      mobile.haptic("success");
      burst();
    }
  };

  const useHint = () => {
    if (!dict) return;
    const last = ladder[ladder.length - 1];
    const path = oneBfsPath(last, end, dict.buckets.get(len)!, len);
    if (!path || path.length < 2) { setShowHint("(no path found — try a different route!)"); return; }
    setShowHint(path[1]);
    setHints(h => h + 1);
    setScore(s => Math.max(0, s - HINT_COST));
    sound.click();
  };

  const giveUp = () => {
    if (!dict) return;
    const path = oneBfsPath(start, end, dict.buckets.get(len)!, len);
    setSolution(path);
    setSolving(true);
    if (timerRef.current) window.clearInterval(timerRef.current);
    setStreak(0);
    setPhase("lost");
    sound.lose();
    mobile.haptic("gameover");
  };

  const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const lowTime = timeLeft <= 15;

  const timePct = useMemo(() => {
    const budget = 20 + par * 18;
    return Math.max(0, Math.min(100, (timeLeft / budget) * 100));
  }, [timeLeft, par]);

  // ---- render ----
  return (
    <div className="container" style={{ padding: "0 16px", maxWidth: 720, margin: "0 auto" }}>
      <a href={`/game-studio/${country}`} className="game-back-link">&larr; Back to Games</a>
      <h1 className="wl-title">🪜 Word Ladders</h1>
      <p className="wl-subtitle">Doublets — invented by Lewis Carroll, 1879. Change one letter at a time to reach the goal.</p>

      {loadError && (
        <div className="wl-notice">⚠️ Online dictionary unavailable — using a small offline word list.</div>
      )}

      {/* score bar */}
      <div className="wl-stats">
        <div className="wl-stat"><span className="wl-stat-label">Score</span><span className="wl-stat-val">{score}</span></div>
        <div className="wl-stat"><span className="wl-stat-label">Streak</span><span className="wl-stat-val wl-fire">{streak > 0 && "🔥 "}{streak}</span></div>
        <div className="wl-stat"><span className="wl-stat-label">Best</span><span className="wl-stat-val">{highScore}</span></div>
        <div className="wl-stat"><span className="wl-stat-label">Top Streak</span><span className="wl-stat-val">{bestStreak}</span></div>
      </div>

      {loading && (
        <div className="wl-loading">Loading the English dictionary<span className="loading-dots" /></div>
      )}

      {/* MENU */}
      {!loading && phase === "menu" && (
        <div className="wl-menu">
          <h2 className="wl-menu-h">Choose your challenge</h2>
          <div className="wl-menu-grid">
            <button className="wl-card" onClick={() => startGame(3, 3)}>
              <div className="wl-card-emoji">🐥</div>
              <h3>Bunny Hop</h3>
              <p>3-letter words · par ~3</p>
              <span className="wl-card-tag">Easy</span>
            </button>
            <button className="wl-card" onClick={() => startGame(4, 4)}>
              <div className="wl-card-emoji">🧩</div>
              <h3>Classic</h3>
              <p>4-letter words · par ~4</p>
              <span className="wl-card-tag">Medium</span>
            </button>
            <button className="wl-card" onClick={() => startGame(5, 5)}>
              <div className="wl-card-emoji">🏔️</div>
              <h3>Mountain</h3>
              <p>5-letter words · par ~5</p>
              <span className="wl-card-tag">Hard</span>
            </button>
            <button className="wl-card wl-card-classic" onClick={() => startGame(4, 4, true)}>
              <div className="wl-card-emoji">🎩</div>
              <h3>Carroll's Classics</h3>
              <p>Famous doublets · 4 letters</p>
              <span className="wl-card-tag wl-tag-gold">Featured</span>
            </button>
            <button className="wl-card wl-card-classic" onClick={() => startGame(5, 5, true)}>
              <div className="wl-card-emoji">🎩</div>
              <h3>Carroll's Classics</h3>
              <p>Famous doublets · 5 letters</p>
              <span className="wl-card-tag wl-tag-gold">Featured</span>
            </button>
          </div>
        </div>
      )}

      {/* PLAYING */}
      {!loading && phase === "playing" && (
        <div className="wl-game">
          <div className="wl-objective">
            <div className="wl-end-word">{start.toUpperCase()}</div>
            <div className="wl-arrow">→</div>
            <div className="wl-end-word wl-goal">{end.toUpperCase()}</div>
          </div>
          {story && <div className="wl-story">“{story}”</div>}

          <div className={`wl-timer ${lowTime ? "wl-timer-low" : ""}`}>
            <div className="wl-timer-bar" style={{ width: `${timePct}%` }} />
            <span className="wl-timer-text">{fmtTime(timeLeft)}</span>
          </div>

          <div className="wl-ladder">
            {ladder.map((w, i) => (
              <div key={i} className={`wl-rung ${i === 0 ? "wl-rung-start" : ""} ${i === ladder.length - 1 && i !== 0 ? "wl-rung-current" : ""}`} style={{ animationDelay: `${i * 30}ms` }}>
                <span className="wl-rung-num">{i + 1}</span>
                <span className="wl-rung-word">{w.toUpperCase()}</span>
                {i === 0 && <span className="wl-rung-tag">start</span>}
                {w === end && <span className="wl-rung-tag wl-rung-tag-done">goal!</span>}
              </div>
            ))}
          </div>

          {showHint && (
            <div className="wl-hint">💡 Try <strong>{showHint.toUpperCase()}</strong> next</div>
          )}

          <div className={`wl-input-row ${shake ? "wl-shake" : ""}`} key={shake} onAnimationEnd={() => setShake(0)}>
            <input
              ref={inputRef}
              className={`wl-input ${badWord ? "wl-input-bad" : ""}`}
              value={input}
              onChange={e => { setInput(e.target.value); setBadWord(false); }}
              onKeyDown={e => { if (e.key === "Enter") submitStep(); }}
              placeholder={`Change one letter of "${ladder[ladder.length - 1].toUpperCase()}"…`}
              maxLength={len}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
            />
            <button className="wl-go" onClick={submitStep}>Climb</button>
          </div>
          {badWord && <div className="wl-err">Not a valid step — must change exactly one letter and be a real word.</div>}

          <div className="wl-actions">
            <button className="wl-btn wl-btn-hint" onClick={useHint}>💡 Hint <span className="wl-cost">-{HINT_COST}</span>{hints > 0 && <span className="wl-cost"> · {hints} used</span>}</button>
            <button className="wl-btn wl-btn-quit" onClick={giveUp}>🏳️ Give Up</button>
            <button className="wl-btn wl-btn-menu" onClick={() => setPhase("menu")}>≡ Menu</button>
          </div>

          <div className="wl-help">
            Par: <strong>{par}</strong> steps · Dictionary: <em>dwyl/english-words</em>
          </div>
        </div>
      )}

      {/* WON */}
      {phase === "won" && (
        <div className="wl-overlay">
          <canvas ref={canvasRef} className="wl-confetti" width={720} height={420} />
          <div className="wl-result">
            <div className="wl-result-emoji">🎉</div>
            <h2 style={{ color: "var(--good)" }}>Summit reached!</h2>
            <p>You climbed {ladder.length - 1} steps (par {par}).</p>
            <div className="wl-result-ladder">
              {ladder.map((w, i) => (
                <React.Fragment key={i}>
                  <span className="wl-result-word">{w.toUpperCase()}</span>
                  {i < ladder.length - 1 && <span className="wl-result-arrow">→</span>}
                </React.Fragment>
              ))}
            </div>
            <div className="wl-actions" style={{ justifyContent: "center" }}>
              <button className="wl-go" onClick={() => startGame(len, par, story ? true : false)}>↻ Another</button>
              <button className="wl-btn wl-btn-menu" onClick={() => setPhase("menu")}>≡ Menu</button>
            </div>
          </div>
        </div>
      )}

      {/* LOST */}
      {phase === "lost" && (
        <div className="wl-overlay">
          <div className="wl-result">
            <div className="wl-result-emoji">🫠</div>
            <h2 style={{ color: "var(--live)" }}>{solving ? "Route revealed" : "Out of time"}</h2>
            <p>{end.toUpperCase()} eluded you this time.</p>
            {solution && (
              <div className="wl-result-ladder">
                {solution.map((w, i) => (
                  <React.Fragment key={i}>
                    <span className="wl-result-word wl-result-word-sol">{w.toUpperCase()}</span>
                    {i < solution.length - 1 && <span className="wl-result-arrow">→</span>}
                  </React.Fragment>
                ))}
              </div>
            )}
            <div className="wl-actions" style={{ justifyContent: "center" }}>
              <button className="wl-go" onClick={() => startGame(len, par, story ? true : false)}>↻ Retry</button>
              <button className="wl-btn wl-btn-menu" onClick={() => setPhase("menu")}>≡ Menu</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
